import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Link,
  Typography,
  useTheme,
} from '@mui/material'
import {
  WifiOff as WifiOffIcon,
  Sync as SyncIcon,
  CloudDone as CloudDoneIcon,
  ErrorOutline as ErrorIcon,
  History as HistoryIcon,
} from '@mui/icons-material'
import { useAppSelector } from '@store/hooks'
import { useSyncManager } from '@hooks/useSyncManager'
import SyncConflictDialog from './SyncConflictDialog'
import SyncHistoryDialog from './SyncHistoryDialog'
import { useShowBottomNav } from '@hooks/useShowBottomNav'
import { useShowMiniSessionBar } from '@hooks/useShowMiniSessionBar'
import { shellFixedBottomOffsetCss, shellOverlayZIndex } from '@components/layout/shellOverlayStack'
import { trackFeature } from '@utils/trackFeature'
import { pendingCountBucket } from './pendingCountBucket'

type OfflineStatusBarProps = {
  /** inline: in-page; fixed: independently positioned; stack: in-flow inside ShellBottomAlertStack. */
  placement?: 'inline' | 'fixed' | 'stack'
}

const OfflineStatusBar: React.FC<OfflineStatusBarProps> = ({ placement = 'inline' }) => {
  const theme = useTheme()
  const showBottomNav = useShowBottomNav()
  const showMiniBar = useShowMiniSessionBar()
  const { isOnline, pendingCount, syncStatus, lastSyncAt, syncError } = useAppSelector(
    (state) => state.offline,
  )
  const { triggerSync, conflicts, history, resolveConflictById } = useSyncManager()
  const [conflictsOpen, setConflictsOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  React.useEffect(() => {
    if (syncStatus === 'success' && pendingCount === 0) {
      setShowSuccess(true)
      const timer = setTimeout(() => setShowSuccess(false), 4000)
      return () => clearTimeout(timer)
    }
    setShowSuccess(false)
  }, [syncStatus, pendingCount])

  const hasConflicts = conflicts.length > 0
  const isVisible =
    !isOnline ||
    pendingCount > 0 ||
    syncStatus === 'error' ||
    syncStatus === 'syncing' ||
    showSuccess ||
    hasConflicts

  const offlineShownRef = useRef(false)
  useEffect(() => {
    if (!isOnline && !offlineShownRef.current) {
      offlineShownRef.current = true
      trackFeature('offline_mode', { action: 'shown' })
    }
    if (isOnline) {
      offlineShownRef.current = false
    }
  }, [isOnline])

  const prevPendingRef = useRef(0)
  useEffect(() => {
    if (!isOnline && pendingCount > prevPendingRef.current) {
      trackFeature('offline_mode', {
        action: 'write_queued',
        count_bucket: pendingCountBucket(pendingCount),
      })
    }
    prevPendingRef.current = pendingCount
  }, [isOnline, pendingCount])

  const lastSyncLabel = lastSyncAt
    ? `Last synced ${new Date(lastSyncAt).toLocaleTimeString()}`
    : 'Never synced'

  const fixedSx =
    placement === 'fixed'
      ? {
          position: 'fixed' as const,
          left: 0,
          right: 0,
          bottom: shellFixedBottomOffsetCss({
            bottomNav: showBottomNav,
            miniSessionBar: showMiniBar,
          }),
          zIndex: shellOverlayZIndex(theme).offlineStatusBar,
          px: { xs: 1, sm: 2 },
          pointerEvents: 'none',
          '& .MuiAlert-root, & .MuiButton-root, & button': {
            pointerEvents: 'auto',
          },
        }
      : undefined

  return (
    <>
      <Collapse in={isVisible}>
        <Box sx={{ mb: placement === 'inline' ? 1 : 0, px: placement === 'stack' ? { xs: 1, sm: 2 } : 0, ...(fixedSx ?? {}) }}>
          {!isOnline && (
            <Alert
              severity="warning"
              icon={<WifiOffIcon fontSize="small" />}
              action={
                pendingCount > 0 ? (
                  <Typography variant="caption" sx={{ alignSelf: 'center', mr: 1 }}>
                    {pendingCount} change{pendingCount !== 1 ? 's' : ''} queued
                  </Typography>
                ) : undefined
              }
              sx={{ mb: 0.5 }}
            >
              You are offline. Cached collection data is available. New changes are saved locally
              and will sync when you reconnect.
            </Alert>
          )}

          {isOnline && hasConflicts && (
            <Alert
              severity="warning"
              icon={<ErrorIcon fontSize="small" />}
              action={
                <Button size="small" color="warning" onClick={() => setConflictsOpen(true)}>
                  Review conflicts
                </Button>
              }
              sx={{ mb: 0.5 }}
            >
              {conflicts.length} sync conflict{conflicts.length !== 1 ? 's' : ''} need your attention.
            </Alert>
          )}

          {isOnline && syncStatus === 'error' && syncError && (
            <Alert
              severity="error"
              icon={<ErrorIcon fontSize="small" />}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {hasConflicts && (
                    <Button size="small" color="error" onClick={() => setConflictsOpen(true)}>
                      Conflicts
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="error"
                    startIcon={<SyncIcon fontSize="small" />}
                    onClick={triggerSync}
                  >
                    Retry
                  </Button>
                </Box>
              }
              sx={{ mb: 0.5 }}
            >
              {syncError}
            </Alert>
          )}

          {isOnline && syncStatus === 'syncing' && (
            <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 0.5 }}>
              Syncing {pendingCount} pending change{pendingCount !== 1 ? 's' : ''}…
            </Alert>
          )}

          {isOnline && syncStatus !== 'syncing' && syncStatus !== 'error' && pendingCount > 0 && (
            <Alert
              severity="info"
              icon={<SyncIcon fontSize="small" />}
              action={
                <Button size="small" color="info" startIcon={<SyncIcon fontSize="small" />} onClick={triggerSync}>
                  Sync now
                </Button>
              }
              sx={{ mb: 0.5 }}
            >
              {pendingCount} offline change{pendingCount !== 1 ? 's' : ''} ready to sync. {lastSyncLabel}.
            </Alert>
          )}

          {isOnline && showSuccess && (
            <Alert
              severity="success"
              icon={<CloudDoneIcon fontSize="small" />}
              action={
                history.length > 0 ? (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setHistoryOpen(true)}
                    sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
                  >
                    View history
                  </Link>
                ) : undefined
              }
              sx={{ mb: 0.5 }}
            >
              All changes synced. {lastSyncLabel}.
            </Alert>
          )}

          {isOnline && syncStatus === 'idle' && pendingCount === 0 && history.length > 0 && !showSuccess && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
              <Button size="small" startIcon={<HistoryIcon fontSize="small" />} onClick={() => setHistoryOpen(true)}>
                Sync history
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>

      <SyncConflictDialog
        open={conflictsOpen}
        conflicts={conflicts}
        onResolve={(id) => resolveConflictById(id)}
        onClose={() => setConflictsOpen(false)}
      />
      <SyncHistoryDialog open={historyOpen} history={history} onClose={() => setHistoryOpen(false)} />
    </>
  )
}

export default OfflineStatusBar
